import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Box,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TableBox } from "@shared/components/Table";
import { SubscriptionOfferDescription } from "@shared/apis/sidekick/generated";
import styled from "@emotion/styled";
import { formatToCurrency } from "@shared/utils/formatters";
import { format } from "date-fns";

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
`;

const AccordionItem = styled(Accordion)`
  border-radius: 1rem !important;
  overflow: hidden;
  &:hover {
    background-color: ${(p) => p.theme.colors.background2};
  }
`;

const CustomAccordionSummary = styled(AccordionSummary)`
  border-radius: 1rem;
  overflow: hidden;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TextGroup = styled(Typography)`
  display: grid;
  gap: 2rem;
  align-items: center;
  grid-template-columns: 180px auto;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  margin-left: 1rem;
  label {
    font-weight: 600;
  }
`;

type Props = {
  subscriptionOffers: SubscriptionOfferDescription[];
  isLoadingSubscriptionOffers: boolean;
};

function formatObjectToLabelValuePairs(object: {
  [key: string]: string;
}): { key: string; label: string; value: string }[] {
  const toTitleCase = (key: string): string =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

  return Object.entries(object).map(([key, value]) => ({
    key,
    label: toTitleCase(key),
    value,
  }));
}

function formatHyphenatedString(input: string): string {
  return input
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export const SubscriptionOffersList = (props: Props) => {
  const { subscriptionOffers, isLoadingSubscriptionOffers } = props;

  return (
    <TableBox>
      <ListContainer>
        {isLoadingSubscriptionOffers && subscriptionOffers.length === 0 && (
          <ContentContainer sx={{ alignItems: "center", padding: "2rem" }}>
            <CircularProgress />
          </ContentContainer>
        )}

        {!isLoadingSubscriptionOffers && subscriptionOffers.length === 0 && (
          <ContentContainer>
            <Typography variant="body2">No subscription plans found</Typography>
          </ContentContainer>
        )}

        {!isLoadingSubscriptionOffers &&
          subscriptionOffers.map((offer) => {
            const rules = formatObjectToLabelValuePairs(offer.otherRules);
            return (
              <AccordionItem
                tabIndex={-1}
                key={offer.id}
                slotProps={{ transition: { unmountOnExit: true } }}
                elevation={0}
              >
                <CustomAccordionSummary
                  expandIcon={<ExpandMoreIcon color="secondary" />}
                  aria-controls="subscription-plan-content"
                  id={`plan-${offer.id}`}
                >
                  <Tooltip title={offer.id}>
                    <Typography variant="h6" fontWeight={600}>
                      {formatHyphenatedString(offer.id)}
                    </Typography>
                  </Tooltip>
                </CustomAccordionSummary>
                <AccordionDetails>
                  <ContentContainer>
                    <Typography variant="h6">General</Typography>
                    <TextGroup>
                      <label>Status</label>{" "}
                      <span>{offer.disabled ? "Disabled" : "Enabled"}</span>
                    </TextGroup>
                    <TextGroup>
                      <label>Price</label>{" "}
                      <span>
                        {isNaN(Number(offer.price))
                          ? offer.price
                          : formatToCurrency(Number(offer.price) / 100)}
                      </span>
                    </TextGroup>

                    <Typography variant="h6" mt={2}>
                      Rules
                    </Typography>

                    {rules.map((rule) => {
                      return (
                        <TextGroup key={rule.key}>
                          <label>{rule.label}</label> <span>{rule.value}</span>
                        </TextGroup>
                      );
                    })}

                    <Typography variant="h6" mt={2}>
                      Validity Period
                    </Typography>

                    <TextGroup>
                      <label>Valid From</label>{" "}
                      <span>
                        {offer.validFrom
                          ? format(offer.validFrom, "MMMM dd, yyyy, h:mm a")
                          : "N/A"}
                      </span>
                    </TextGroup>
                    <TextGroup>
                      <label>Valid Until</label>{" "}
                      <span>
                        {offer.validUntil
                          ? format(offer.validUntil, "MMMM dd, yyyy, h:mm a")
                          : "N/A"}
                      </span>
                    </TextGroup>
                  </ContentContainer>
                </AccordionDetails>
              </AccordionItem>
            );
          })}
      </ListContainer>
    </TableBox>
  );
};
